import { useCallback, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import * as buttonTracking from './button'
import * as dialogTracking from './dialog'
import * as dragTracking from './drag'
import * as formTracking from './form'
import * as inputTracking from './input'
import * as menuTracking from './menu'
import { PostHog, usePostHog } from 'posthog-js/react'
import { useRegister } from './use-register'
import { useIdentify } from './use-identify'
import { useMe } from '../me'
import decamelizeKeys from 'decamelize-keys'
import { PermissionRole } from '../../generated/graphql'

const roleMappings = new Map<PermissionRole | undefined, string>([
  [PermissionRole.SaGlobal, 'sa-global'],
  [PermissionRole.SaLocal, 'sa-local'],
  [PermissionRole.Admin, 'admin'],
  [PermissionRole.Staff, 'staff'],
  [PermissionRole.StoreManager, 'store-manager'],
  [PermissionRole.StoreStaff, 'store-staff'],
  [PermissionRole.Viewer, 'viewer'],
])

export type PageData = {
  path: string
  search: string
  hash: string
  url: string
  previousPath: string | undefined
  previousSearch: string | undefined
  previousHash: string | undefined
  previousUrl: string | undefined
}

export type Metadata = {
  name: string
  [key: string]: string | number | boolean | undefined | null
}

export type ExtendedMetadata = {
  category: string
} & Metadata

export type CaptureWithContext = (
  name: string,
  metadata?: ExtendedMetadata,
) => ReturnType<PostHog['capture']>

export const useTracking = () => {
  const postHog = usePostHog()
  const location = useLocation()
  const { siteId } = useMe()
  const { me } = useMe()

  useRegister()
  useIdentify()

  const sharedMetadata = useMemo(
    () => ({
      userRole: roleMappings.get(me?.role),
      siteId: siteId?.toString(),
    }),
    [me?.role, siteId],
  )

  const captureWithContext: CaptureWithContext = useCallback(
    (name: string, metadata?: Metadata) => {
      const href = window.location.href
      const payload = metadata
        ? decamelizeKeys({
            ...sharedMetadata,
            ...metadata,
            path: location.pathname,
            search: location.search,
            hash: location.hash,
            url: href,
          })
        : undefined
      return postHog.capture(name, payload)
    },
    [
      location.hash,
      location.pathname,
      location.search,
      postHog,
      sharedMetadata,
    ],
  )

  const trackButtonClick = useMemo(
    () => buttonTracking.trackButtonClick(captureWithContext),
    [captureWithContext],
  )

  const trackButtonClickEvent = useMemo(
    () => buttonTracking.trackButtonClickEvent(captureWithContext),
    [captureWithContext],
  )

  const trackInput = useMemo(
    () => inputTracking.trackInput(captureWithContext),
    [captureWithContext],
  )

  const trackInputFocus = useMemo(
    () => inputTracking.trackInputFocus(captureWithContext),
    [captureWithContext],
  )

  const trackInputBlur = useMemo(
    () => inputTracking.trackInputBlur(captureWithContext),
    [captureWithContext],
  )

  const trackInputChange = useMemo(
    () => inputTracking.trackInputChange(captureWithContext),
    [captureWithContext],
  )

  const trackDialogOpen = useMemo(
    () => dialogTracking.trackDialogOpen(captureWithContext),
    [captureWithContext],
  )

  const trackDialogClose = useMemo(
    () => dialogTracking.trackDialogClose(captureWithContext),
    [captureWithContext],
  )

  const trackFormSuccess = useMemo(
    () => formTracking.trackFormSuccess(captureWithContext),
    [captureWithContext],
  )

  const trackFormError = useMemo(
    () => formTracking.trackFormError(captureWithContext),
    [captureWithContext],
  )

  const trackDragStart = useMemo(
    () => dragTracking.trackDragStart(captureWithContext),
    [captureWithContext],
  )

  const trackDragEnd = useMemo(
    () => dragTracking.trackDragEnd(captureWithContext),
    [captureWithContext],
  )

  const trackMenuOpen = useMemo(
    () => menuTracking.trackMenuOpen(captureWithContext),
    [captureWithContext],
  )

  const trackMenuClose = useMemo(
    () => menuTracking.trackMenuClose(captureWithContext),
    [captureWithContext],
  )

  const track = useCallback(
    (name: string, metadata: ExtendedMetadata) => {
      void captureWithContext(name, metadata)
    },
    [captureWithContext],
  )

  const reset = useCallback(() => postHog.reset(), [postHog])

  const page = useCallback(
    (pageData: PageData) =>
      postHog.capture(
        '$pageview',
        decamelizeKeys({
          ...sharedMetadata,
          ...pageData,
        }),
      ),
    [postHog, sharedMetadata],
  )

  return {
    reset,
    page,
    track,
    trackButtonClick,
    trackButtonClickEvent,
    trackInput,
    trackInputFocus,
    trackInputBlur,
    trackInputChange,
    trackDialogOpen,
    trackDialogClose,
    trackFormSuccess,
    trackFormError,
    trackDragStart,
    trackDragEnd,
    trackMenuOpen,
    trackMenuClose,
  }
}
